import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchIntroductionScreens = createAsyncThunk(
    "introductionScreens/fetchList",
    async ({ search = "", page = 1, limit = 10 }, { rejectWithValue }) => {
        try {
            const res = await Axios.get(
                `/introduction-screens?search=${search}&page=${page}&limit=${limit}`,
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchIntroductionScreenById = createAsyncThunk(
    "introductionScreens/fetchById",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.get(`/introduction-screens/${id}`, authHeaders());
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const createIntroductionScreen = createAsyncThunk(
    "introductionScreens/create",
    async (formData, { rejectWithValue }) => {
        try {
            const auth = authHeaders();
            const res = await Axios.post("/introduction-screens", formData, {
                headers: {
                    ...auth.headers,
                    "Content-Type": "multipart/form-data",
                },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateIntroductionScreen = createAsyncThunk(
    "introductionScreens/update",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const auth = authHeaders();
            const res = await Axios.patch(`/introduction-screens/${id}`, formData, {
                headers: {
                    ...auth.headers,
                    "Content-Type": "multipart/form-data",
                },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const deleteIntroductionScreen = createAsyncThunk(
    "introductionScreens/delete",
    async (id, { rejectWithValue }) => {
        try {
            const res = await Axios.delete(`/introduction-screens/${id}`, authHeaders());
            return { id, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const toggleIntroductionScreenStatus = createAsyncThunk(
    "introductionScreens/toggleStatus",
    async ({ id, is_active }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/introduction-screens/${id}/status`,
                { is_active },
                authHeaders()
            );
            return { id, is_active, res: res.data };
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const reorderIntroductionScreen = createAsyncThunk(
    "introductionScreens/reorder",
    async ({ id, order }, { rejectWithValue }) => {
        try {
            const res = await Axios.patch(
                `/introduction-screens/${id}/reorder`,
                { order },
                authHeaders()
            );
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

const introductionScreenSlice = createSlice({
    name: "introductionScreens",
    initialState: {
        list: [],
        currentScreen: null,
        total: 0,
        isLoading: false,
        error: null,
    },
    reducers: {
        clearCurrentScreen: (state) => {
            state.currentScreen = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIntroductionScreens.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchIntroductionScreens.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload?.data?.data || [];
                state.total = action.payload?.data?.pagination?.total || 0;
            })
            .addCase(fetchIntroductionScreens.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(fetchIntroductionScreenById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchIntroductionScreenById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentScreen = action.payload?.data || null;
            })
            .addCase(fetchIntroductionScreenById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(deleteIntroductionScreen.fulfilled, (state, action) => {
                state.list = state.list.filter((item) => item.id !== action.payload.id);
                state.total -= 1;
            })
            .addCase(toggleIntroductionScreenStatus.fulfilled, (state, action) => {
                const { id, is_active } = action.payload;
                const screen = state.list.find((item) => item.id === id);
                if (screen) {
                    screen.is_active = is_active;
                }
            })
            .addCase(reorderIntroductionScreen.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(reorderIntroductionScreen.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(reorderIntroductionScreen.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearCurrentScreen } = introductionScreenSlice.actions;
export default introductionScreenSlice.reducer;
